import { Box, CircularProgress, Typography } from "@mui/material";
import SectionCollapsible from "../../../common/SectionCollapsible";
import ReadOnlyRiskIncidentData from "./incident-table/ReadOnlyRiskMonitoringData";
import MapComponent from "./map";
import { useState, useMemo } from "react";
import useRiskIncidentsDetails from "../../hooks/useRiskIncidentsDetails";
import { createRiskIncidentRows } from "./incident-table/RiskIncidentRows";
import { RiskMonitoring } from "../../hooks/useAddSolutionForm";
import { IncidentTableData } from "./types";
import { ReadOnlyRiskMonitoring } from "../ReadOnlyRiskMonitoringContainer";

export const configureDatamessage = `Project incident monitoring has not been configured. Please upload project area and configure the monitoring zone if alerts are required for this project.`;

function ReadOnlyRiskIncidentSection({
  solutionId,
  riskMonitoringInfo,
}: {
  solutionId: string;
  riskMonitoringInfo: RiskMonitoring;
}) {
  const { details, error, loading } = useRiskIncidentsDetails(solutionId);
  const [page, setPage] = useState(0);
  const [selectedIncident, setSelectedIncident] =
    useState<IncidentTableData | null>(null);
  const [selectedIncidents, setSelectedIncidents] = useState<
    Array<IncidentTableData>
  >([]);
  const rowsPerPage = 10;
  const tableData = details?.tableData;
  const shapeCreatedAt = details?.initialShapeCreatedAt;
  const rows = useMemo(() => {
    const arrayDetails = Array.isArray(tableData) ? tableData : [];
    return createRiskIncidentRows(arrayDetails);
  }, [tableData]);

  if (error) {
    return (
      <Box sx={{ padding: "32px", placeSelf: "center" }}>
        <Typography sx={{ fontSize: "15px" }} color="error">
          Error Retrieving Solution Risk Incident Data.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        data-testid="solution-details-loading-indicator"
        sx={{
          minHeight: "10vh",
          display: "flex",
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    !rows.length &&
    typeof riskMonitoringInfo?.geospatialDataId === "string"
  ) {
    return (
      <Box data-testid="read-only-risk-monitoring-container">
        <Box sx={{ paddingBottom: 2 }}>
          <ReadOnlyRiskMonitoring
            riskMonitoring={riskMonitoringInfo}
            shapeCreatedAt={shapeCreatedAt}
          />
        </Box>
        <Box sx={{ paddingBottom: 2 }}>
          <MapComponent
            outline={details?.outline}
            bufferArea={details?.bufferArea}
            selectedIncident={selectedIncident}
            selectedIncidents={selectedIncidents}
          />
        </Box>
        <Typography
          sx={{ fontSize: "18px", padding: "32px", placeSelf: "center" }}
        >
          No incidents within project area.
        </Typography>
      </Box>
    );
  }
  if (!rows.length && !riskMonitoringInfo?.geospatialDataId) {
    return (
      <Box sx={{ padding: "32px", placeSelf: "center" }}>
        <Box sx={{ paddingBottom: 2 }}>
          <ReadOnlyRiskMonitoring
            riskMonitoring={riskMonitoringInfo}
            shapeCreatedAt={shapeCreatedAt}
          />
        </Box>
        <Typography sx={{ fontSize: "18px" }}>
          {configureDatamessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="read-only-risk-monitoring-container">
      <Box sx={{ paddingBottom: 2 }}>
        <ReadOnlyRiskMonitoring
          riskMonitoring={riskMonitoringInfo}
          shapeCreatedAt={shapeCreatedAt}
        />
      </Box>
      <Box sx={{ paddingBottom: 2 }}>
        <MapComponent
          outline={details?.outline}
          bufferArea={details?.bufferArea}
          selectedIncident={selectedIncident}
          selectedIncidents={selectedIncidents}
        />
      </Box>
      <SectionCollapsible label="Risk Incident Data">
        <ReadOnlyRiskIncidentData
          solutionId={solutionId}
          rows={rows}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          selectedIncident={selectedIncident}
          selectedIncidents={selectedIncidents}
          setSelectedIncident={setSelectedIncident}
          setSelectedIncidents={setSelectedIncidents}
        />
      </SectionCollapsible>
    </Box>
  );
}

export default ReadOnlyRiskIncidentSection;
